import { useEffect, useRef, useState } from 'react';

const TARGET_PETS = 14;
const VISUAL_STAGES = 6;
const WIN_PAUSE_MS = 650;

let petAudioContext;

const getPetAudioContext = () => {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return null;

  if (!petAudioContext) {
    petAudioContext = new AudioContext();
  }

  if (petAudioContext.state === 'suspended') {
    petAudioContext.resume().catch(() => {});
  }

  return petAudioContext;
};

const playPetSound = (happiness, goal) => {
  const context = getPetAudioContext();
  if (!context) return;

  const now = context.currentTime;
  const progress = Math.min(1, happiness / Math.max(goal, 1));
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  // Soft boop that gets a little brighter as the dawg gets happier.
  const start = 320 + progress * 220;
  const peak = 520 + progress * 420;

  oscillator.type = 'triangle';
  oscillator.frequency.setValueAtTime(start, now);
  oscillator.frequency.exponentialRampToValueAtTime(peak, now + 0.05);
  oscillator.frequency.exponentialRampToValueAtTime(start * 0.9, now + 0.12);

  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.055 + progress * 0.025, now + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(now);
  oscillator.stop(now + 0.16);
};

function PetDawgGame({
  active = true,
  locked = false,
  targetPets = TARGET_PETS,
  onWin,
  levelKey,
}) {
  const [pets, setPets] = useState(0);
  const [bounceKey, setBounceKey] = useState(0);

  const petsRef = useRef(0);
  const wonRef = useRef(false);
  const activeRef = useRef(active);
  const lockedRef = useRef(locked);
  const targetRef = useRef(Number(targetPets) || TARGET_PETS);
  const onWinRef = useRef(onWin);
  const winTimerRef = useRef(0);

  activeRef.current = active;
  lockedRef.current = locked;
  targetRef.current = Number(targetPets) || TARGET_PETS;
  onWinRef.current = onWin;

  useEffect(() => {
    if (winTimerRef.current) {
      window.clearTimeout(winTimerRef.current);
      winTimerRef.current = 0;
    }
    petsRef.current = 0;
    wonRef.current = false;
    setPets(0);
    setBounceKey(0);
  }, [levelKey]);

  useEffect(
    () => () => {
      if (winTimerRef.current) window.clearTimeout(winTimerRef.current);
    },
    []
  );

  const goal = Number(targetPets) || TARGET_PETS;
  const canPet =
    active && !locked && !wonRef.current && pets < goal;
  const stage =
    pets <= 0
      ? 0
      : pets >= goal
        ? VISUAL_STAGES
        : Math.max(1, Math.ceil((pets / goal) * VISUAL_STAGES));
  const isMax = pets >= goal;

  const handlePet = () => {
    if (!activeRef.current || lockedRef.current || wonRef.current) return;

    const next = Math.min(goal, petsRef.current + 1);
    petsRef.current = next;
    setPets(next);
    setBounceKey((key) => key + 1);
    playPetSound(next, goal);

    if (next >= goal) {
      wonRef.current = true;
      winTimerRef.current = window.setTimeout(() => {
        onWinRef.current?.();
      }, WIN_PAUSE_MS);
    }
  };

  return (
    <div className="pet-dawg">
      <p className="question-text">Pet that dawg!</p>
      <p className="pet-dawg-hint">
        {isMax ? 'So happy!!!!' : 'Keep petting…'}
      </p>
      <button
        type="button"
        className={`pet-dawg-stage pet-dawg-stage-${stage} ${isMax ? 'is-max' : ''}`}
        aria-label={isMax ? 'Very happy dog' : 'Pet the dog'}
        disabled={!canPet}
        onClick={handlePet}
      >
        <span key={bounceKey} className="pet-dawg-bounce" aria-hidden="true">
          <span className="pet-dawg-tail" />
          <span className="pet-dawg-body">
            <span className="pet-dawg-ear pet-dawg-ear-left" />
            <span className="pet-dawg-ear pet-dawg-ear-right" />
            <span className="pet-dawg-face">
              <span className="pet-dawg-eye pet-dawg-eye-left" />
              <span className="pet-dawg-eye pet-dawg-eye-right" />
              <span className="pet-dawg-blush pet-dawg-blush-left" />
              <span className="pet-dawg-blush pet-dawg-blush-right" />
              <span className="pet-dawg-snout">
                <span className="pet-dawg-nose" />
                <span className="pet-dawg-mouth">
                  <span className="pet-dawg-tongue" />
                </span>
              </span>
            </span>
          </span>
          <span className="pet-dawg-hearts">
            <span className="pet-dawg-heart">♥</span>
            <span className="pet-dawg-heart">♥</span>
            <span className="pet-dawg-heart">♥</span>
          </span>
        </span>
      </button>
    </div>
  );
}

export default PetDawgGame;
